import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useServices } from '../../app/services';
import { todayIso } from '../../data/store/store';
import { convertMinor, rateFor, type FxRate } from '../../data/fx';
import { currencyExponent } from '../../domain/money';
import { formatDayMonth } from '../../i18n/format';
import { parseAmount, type ParsedAmount } from '../../i18n/parseAmount';
import { CURRENCIES, currencyName } from '../../i18n/regions';
import { AmountInput, SelectField, useFormat } from '../../ui';

/**
 * Paid in another currency (a trip, an online order): the person types what
 * they paid, in that currency; the entry's amount is its value in the
 * budget's currency, from the ECB rate for the day (see data/fx.ts) or typed
 * by hand when there is no rate. Totals only ever add the budget's currency.
 */

/** The amount as someone in the budget's region would type it: 1840,00 in Türkiye, 1840.00 in the US. */
export const amountText = (minor: number, currency: string, locale: string) => {
  const exp = currencyExponent(currency);
  return new Intl.NumberFormat(locale, { minimumFractionDigits: exp, maximumFractionDigits: exp, useGrouping: false }).format(minor / 10 ** exp);
};

export function PaidInField({ budgetCurrency, value, onChange }: { budgetCurrency: string; value: string; onChange: (currency: string) => void }) {
  const { t } = useTranslation();
  const { language } = useServices();
  const others = CURRENCIES.filter((c) => c !== budgetCurrency);
  return (
    <SelectField label={t('paidIn.label')} hint={value === budgetCurrency ? t('paidIn.hint') : undefined} value={value} onChange={(e) => onChange(e.target.value)}>
      <option value={budgetCurrency}>
        {currencyName(budgetCurrency, language)} ({budgetCurrency})
      </option>
      {others.map((c) => (
        <option key={c} value={c}>
          {currencyName(c, language)} ({c})
        </option>
      ))}
    </SelectField>
  );
}

export interface Conversion {
  /** The entry's amount in the budget's currency: typed by hand, else from the rate; null while unknown. */
  amountMinor: number | null;
  /** Nothing to save yet: no rate and nothing typed, or the typed value is not an amount. */
  missing: boolean;
}

/**
 * The paid amount's value in the budget's currency, from the day's rate and
 * editable. A typed value (or an edited entry's saved one, see `type`) stays
 * until `reset`, which the caller does when what was paid changes.
 */
export function useConversion(paidMinor: number | null, paidIn: string, budgetCurrency: string, date: string) {
  const { db } = useServices();
  const [rate, setRate] = useState<FxRate | null | 'loading'>(null);
  const [typed, setTyped] = useState<{ text: string; parsed: ParsedAmount } | null>(null);
  const foreign = paidIn !== budgetCurrency;

  useEffect(() => {
    if (!foreign) return setRate(null);
    let live = true;
    setRate('loading');
    void rateFor(db, paidIn, budgetCurrency, date || todayIso(), todayIso()).then((r) => live && setRate(r));
    return () => {
      live = false;
    };
  }, [db, foreign, paidIn, budgetCurrency, date]);

  const reset = useCallback(() => setTyped(null), []);
  const type = useCallback((text: string, parsed: ParsedAmount) => setTyped({ text, parsed }), []);
  const fromRate = foreign && rate && rate !== 'loading' && paidMinor ? convertMinor(paidMinor, paidIn, budgetCurrency, rate.rate) : null;
  const amountMinor = typed ? (typeof typed.parsed === 'number' && typed.parsed > 0 ? typed.parsed : null) : fromRate;
  return {
    foreign,
    rate,
    fromRate,
    typed,
    /** A change to what was paid: the value follows the rate again. */
    reset,
    type,
    result: { amountMinor, missing: foreign && amountMinor === null } satisfies Conversion,
  };
}

/** The converted amount under the paid one: the rate it came from, and a field to put in what the bank charged. */
export function ConvertedAmount({ conversion, paidIn, budgetCurrency, showError }: { conversion: ReturnType<typeof useConversion>; paidIn: string; budgetCurrency: string; showError?: boolean }) {
  const { t } = useTranslation();
  const { language } = useServices();
  const { formatLocale } = useFormat();
  const { rate, fromRate, typed } = conversion;
  const value = typed ? typed.text : fromRate !== null ? amountText(fromRate, budgetCurrency, formatLocale) : '';
  const rateText = (r: FxRate) => {
    const exp = Math.min(4, Math.max(2, 4 - Math.floor(Math.log10(r.rate))));
    const shown = new Intl.NumberFormat(formatLocale, { maximumFractionDigits: exp }).format(r.rate);
    return t(r.stale ? 'paidIn.rateStale' : 'paidIn.rate', { from: paidIn, to: budgetCurrency, rate: shown, date: formatDayMonth(r.date, language) });
  };
  const hint = rate === 'loading' ? t('paidIn.loading') : rate ? `${rateText(rate)} ${t('paidIn.editHint')}` : t('paidIn.noRate', { from: paidIn, to: budgetCurrency });
  const invalid = typed && typed.parsed === 'invalid';
  return (
    <AmountInput
      label={t('paidIn.inBudget', { currency: budgetCurrency })}
      currency={budgetCurrency}
      secondary
      value={value}
      onValueChange={(v) => conversion.type(v, parseAmount(v, budgetCurrency))}
      hint={hint}
      error={invalid ? t('quickAdd.invalid') : showError && conversion.result.missing ? t('paidIn.required', { currency: budgetCurrency }) : undefined}
    />
  );
}
